import React, { useRef, useState } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonCard, IonCardContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const emojis = ["🍎", "🎈", "🚗", "🐶", "🦄", "🏀", "🍪", "🐟", "🍇", "🌻"];

const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateQuestion = () => {
    const num1 = getRandomNumber(1, 9);
    const num2 = getRandomNumber(1, 9);
    const correctAnswer = num1 + num2;
    const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];

    const options = new Set<number>();
    options.add(correctAnswer);
    while (options.size < 3) {
        options.add(getRandomNumber(2, 10));
    }

    return {
        question: `${num1} + ${num2} = ?`,
        correctAnswer,
        options: Array.from(options).sort(() => Math.random() - 0.5), // Shuffle options
        emoji: randomEmoji,
        num1,
        num2,
    };
};

const PictureAddition: React.FC = () => {
    const [quiz, setQuiz] = useState(generateQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    document.title = "Picture Addition Worksheets for UKG | Visual Math for Early Learners";

    const handleAnswerClick = (option: number) => {
        if (option === quiz.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                setQuiz(generateQuestion())
                setShowImage(false);
                setFeedback("")
            }, 5000)
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach your UKG child to add using fun Picture Addition worksheets. Visual math using objects, fruits & shapes to build strong number skills. Free downloads!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/addition/picture-addition" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Picture Addition</h1>

                                    <p>
                                        Make learning addition fun and engaging for young minds with <strong>Picture Addition worksheets</strong>! Designed especially for UKG students, these activities help children understand the concept of <strong>adding two groups of objects</strong> using visuals like fruits, toys, animals, and shapes.
                                    </p>

                                    <h2>🔍 What Is Picture Addition?</h2>
                                    <p>
                                        Picture Addition is a beginner-friendly math method that uses images to teach counting and combining. Instead of just numbers, kids see pictures of objects to understand the meaning of addition.
                                    </p>

                                    <p><strong>Example:</strong> 🍎🍎 + 🍎🍎🍎 = <strong>5 Apples</strong></p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand the concept of “putting together” or combining</li>
                                        <li>Count groups of objects accurately</li>
                                        <li>Build confidence in basic addition (1–10)</li>
                                        <li>Bridge visual understanding to numeric equations</li>
                                    </ul>


                                    <h2>🧠 Why Picture Addition Works</h2>
                                    <ul>
                                        <li>Builds visual memory and numeric fluency</li>
                                        <li>Helps children connect real-life objects with numbers</li>
                                        <li>Encourages independent problem-solving through images</li>
                                    </ul>



                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🏠 Try This at Home!</h2>
                                    <ul>
                                        <li>Use fruits: <strong>“You have 2 bananas and I give you 3 more. How many do you have?”</strong></li>
                                        <li>Use building blocks or toy cars to create visual addition problems</li>
                                        <li>Draw two sets of stars and ask your child to count them together</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Picture Addition</h1>

                                <p>
                                    Make learning addition fun and engaging for young minds with <strong>Picture Addition worksheets</strong>! Designed especially for UKG students, these activities help children understand the concept of <strong>adding two groups of objects</strong> using visuals like fruits, toys, animals, and shapes.
                                </p>

                                <h2>🔍 What Is Picture Addition?</h2>
                                <p>
                                    Picture Addition is a beginner-friendly math method that uses images to teach counting and combining. Instead of just numbers, kids see pictures of objects to understand the meaning of addition.
                                </p>

                                <p><strong>Example:</strong> 🍎🍎 + 🍎🍎🍎 = <strong>5 Apples</strong></p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the concept of “putting together” or combining</li>
                                    <li>Count groups of objects accurately</li>
                                    <li>Build confidence in basic addition (1–10)</li>
                                    <li>Bridge visual understanding to numeric equations</li>
                                </ul>


                                <h2>🧠 Why Picture Addition Works</h2>
                                <ul>
                                    <li>Builds visual memory and numeric fluency</li>
                                    <li>Helps children connect real-life objects with numbers</li>
                                    <li>Encourages independent problem-solving through images</li>
                                </ul>



                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🏠 Try This at Home!</h2>
                                <ul>
                                    <li>Use fruits: <strong>“You have 2 bananas and I give you 3 more. How many do you have?”</strong></li>
                                    <li>Use building blocks or toy cars to create visual addition problems</li>
                                    <li>Draw two sets of stars and ask your child to count them together</li>
                                </ul>
                            </article>
                            <h2>How many in total?</h2>
                            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                                {Array(quiz.num1).fill(quiz.emoji).join(" ")}
                            </div>
                            <h3>+</h3>
                            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                                {Array(quiz.num2).fill(quiz.emoji).join(" ")}
                            </div>
                            <h3>= ?</h3>
                            <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {quiz.options.map((option, index) => (
                                    <div className="background-blue" key={index} onClick={() => handleAnswerClick(option)} style={{ margin: "5px", padding: "10px", width: "60px" }}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {feedback && <p>{feedback}</p>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default PictureAddition;
